import { useState, useMemo, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Mail, FileText } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { TermsModal, TermsVariables } from "./TermsModal";
import { ContestEntryField } from "@/hooks/useContest";
import { DateSelectField } from "./DateSelectField";
import {
  dateFieldError,
  getCustomInputType,
  getDateMode,
  getDateRange,
  getDropdownOptions,
} from "@/lib/entryFields";
import { logEntryAttempt } from "@/lib/entryAttempts";

interface EmailCollectorProps {
  contestId?: string;
  onSubmit: (email: string, fieldsPayload: Record<string, any>) => Promise<void>;
  isLoading?: boolean;
  themeColors?: {
    buttonBackground?: string;
    buttonText?: string;
  };
  requireTermsAcceptance?: boolean;
  termsVariables?: TermsVariables;
  referralBonusEntries?: number;
  endsAt?: string | null;
  startsAt?: string | null;
  useCustomTerms?: boolean;
  customTermsText?: string | null;
  entryFields?: ContestEntryField[];
  legalPrivacyUrl?: string | null;
  legalTermsUrl?: string | null;
  consentHelper?: string | null;
}

// Helper to darken a hex color for hover states
function darkenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.max(0, (num >> 16) - amt);
  const G = Math.max(0, ((num >> 8) & 0x00ff) - amt);
  const B = Math.max(0, (num & 0x0000ff) - amt);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

export function EmailCollector({
  contestId,
  onSubmit,
  isLoading,
  themeColors,
  requireTermsAcceptance,
  termsVariables,
  referralBonusEntries,
  endsAt,
  startsAt,
  useCustomTerms,
  customTermsText,
  entryFields = [],
  legalPrivacyUrl,
  legalTermsUrl,
  consentHelper,
}: EmailCollectorProps) {
  const [email, setEmail] = useState("");
  const [fieldValues, setFieldValues] = useState<Record<string, string | boolean>>({});
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isHovered, setIsHovered] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Auto-populate United States if address is an enabled field
  useEffect(() => {
    const hasAddressField = entryFields.some(f => f.field_type === "address");
    if (hasAddressField && !fieldValues["address_country"]) {
      setFieldValues(prev => ({ ...prev, address_country: "United States" }));
    }
  }, [entryFields]);

  // Email validation regex: requires standard format with at least 2 chars after TLD dot
  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const isValidEmail = useMemo(() => {
    if (!email) return false;
    return EMAIL_REGEX.test(email.trim());
  }, [email]);

  const canSubmit = useMemo(() => {
    if (!isValidEmail) return false;
    if (requireTermsAcceptance && !termsAccepted) return false;
    return true;
  }, [isValidEmail, requireTermsAcceptance, termsAccepted]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const trimmedEmail = email.trim().toLowerCase();

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (requireTermsAcceptance && !termsAccepted) {
      setError("You must accept the Terms & Conditions to enter.");
      return;
    }

    // Validate Dynamic Fields
    let hasValidationError = false;
    const newFormErrors: Record<string, string> = {};

    entryFields.forEach((field) => {
      if (field.is_required) {
        if (field.field_type === "address") {
          const reqAddressFields = ['address_line1', 'address_city', 'address_state', 'address_zip'];
          for (const key of reqAddressFields) {
            if (!fieldValues[key] || String(fieldValues[key]).trim() === "") {
              hasValidationError = true;
              newFormErrors[key] = key === 'address_state' ? "Please select a state." : "Required";
            } else if (key === 'address_zip' && String(fieldValues[key]).trim().length !== 5) {
              hasValidationError = true;
              newFormErrors[key] = "Please enter a valid 5-digit ZIP code.";
            }
          }
        } else if (field.field_type === "email_opt_in" || field.field_type === "sms_opt_in") {
          if (!fieldValues[field.field_key]) {
            hasValidationError = true;
            newFormErrors[field.field_key] = "Required";
          }
        } else if (field.field_type === "custom") {
          const inputType = getCustomInputType(field);
          const raw = fieldValues[field.field_key];
          if (inputType === "checkbox") {
            if (!raw) {
              hasValidationError = true;
              newFormErrors[field.field_key] = "Required";
            }
          } else if (inputType === "dropdown") {
            const options = getDropdownOptions(field);
            const value = String(raw || "").trim();
            if (!value || (options.length > 0 && !options.includes(value))) {
              hasValidationError = true;
              newFormErrors[field.field_key] = "Please select an option.";
            }
          } else if (inputType === "date") {
            const value = String(raw || "").trim();
            const dateError = dateFieldError(value, getDateMode(field), getDateRange(field), true);
            if (dateError) {
              hasValidationError = true;
              newFormErrors[field.field_key] = dateError;
            }
          } else if (!raw || String(raw).trim() === "") {
            hasValidationError = true;
            newFormErrors[field.field_key] = "Required";
          }
        } else if (field.field_type === "name") {
          const first = String(fieldValues["first_name"] || "").trim();
          const last = String(fieldValues["last_name"] || "").trim();
          if (!first) {
            hasValidationError = true;
            newFormErrors["first_name"] = "Required";
          }
          if (!last) {
            hasValidationError = true;
            newFormErrors["last_name"] = "Required";
          }
        } else if (field.field_type === "phone") {
          const phoneVal = (fieldValues[field.field_key] as string) || "";
          if (!phoneVal || phoneVal.trim() === "") {
            hasValidationError = true;
            newFormErrors[field.field_key] = "Phone number is required.";
          } else if (phoneVal.length !== 10) {
            hasValidationError = true;
            newFormErrors[field.field_key] = "Please enter a valid 10-digit phone number.";
          }
        } else {
          if (!fieldValues[field.field_key] || String(fieldValues[field.field_key]).trim() === "") {
            hasValidationError = true;
            newFormErrors[field.field_key] = "Required";
          }
        }
      } else {
        // Even if not required, validate format if provided
        if (field.field_type === "phone") {
          const phoneVal = (fieldValues[field.field_key] as string) || "";
          if (phoneVal && phoneVal.length !== 10) {
            hasValidationError = true;
            newFormErrors[field.field_key] = "Please enter a valid 10-digit phone number.";
          }
        } else if (field.field_type === "address") {
          const zipVal = (fieldValues["address_zip"] as string) || "";
          if (zipVal && zipVal.trim().length !== 5) {
            hasValidationError = true;
            newFormErrors["address_zip"] = "Please enter a valid 5-digit ZIP code.";
          }
        } else if (field.field_type === "custom" && getCustomInputType(field) === "date") {
          const value = String(fieldValues[field.field_key] || "").trim();
          const dateError = dateFieldError(value, getDateMode(field), getDateRange(field), false);
          if (dateError) {
            hasValidationError = true;
            newFormErrors[field.field_key] = dateError;
          }
        }
      }
    });

    const firstName = String(fieldValues["first_name"] || "").trim();
    const lastName = String(fieldValues["last_name"] || "").trim();
    const fieldsPayload = {
      name: [firstName, lastName].filter(Boolean).join(" ") || undefined,
      first_name: firstName || undefined,
      last_name: lastName || undefined,
      phone: fieldValues["phone"] as string || undefined,
      address_line1: fieldValues["address_line1"] as string || undefined,
      address_line2: fieldValues["address_line2"] as string || undefined,
      address_city: fieldValues["address_city"] as string || undefined,
      address_state: fieldValues["address_state"] as string || undefined,
      address_zip: fieldValues["address_zip"] as string || undefined,
      address_country: fieldValues["address_country"] as string || undefined,
      email_opt_in: fieldValues["email_opt_in"] as boolean || false,
      sms_opt_in: fieldValues["sms_opt_in"] as boolean || false,
      custom: Object.fromEntries(
        Object.entries(fieldValues).filter(([key]) => key.startsWith("custom_"))
      ),
    };

    const saveAttempt = (message: string) => {
      if (!contestId) return;
      void logEntryAttempt({
        contestId,
        email: trimmedEmail,
        payload: fieldsPayload,
        error: message,
      });
    };

    if (hasValidationError) {
      const message = "Please fill in all required fields before continuing.";
      setFormErrors(newFormErrors);
      setError(message);
      saveAttempt(message);
      return;
    }

    try {
      await onSubmit(trimmedEmail, fieldsPayload);
      // Reset after success if needed, but usually we unmount or show success state
    } catch (err) {
      const message = err instanceof Error ? err.message : "An error occurred";
      setError(message);
      saveAttempt(message);
    }
  };

  const handleFieldValueChange = (key: string, value: string | boolean) => {
    setFieldValues(prev => ({ ...prev, [key]: value }));
    setFormErrors(prev => {
      if (prev[key]) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return prev;
    });
    if (error === "Please fill in all required fields before continuing.") {
      setError("");
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) setError(""); // Clear error as user types
  };

  // Compute inline styles for theme colors (same logic as EntryButton)
  const buttonStyles = useMemo(() => {
    if (themeColors?.buttonBackground && themeColors?.buttonText) {
      const bgColor = themeColors.buttonBackground;
      const hoverBg = darkenColor(bgColor, 15);

      return {
        backgroundColor: isHovered && !isLoading && canSubmit ? hoverBg : bgColor,
        color: themeColors.buttonText,
        borderColor: bgColor,
        opacity: (!canSubmit || isLoading) ? 0.6 : 1,
        cursor: (!canSubmit || isLoading) ? 'not-allowed' : 'pointer',
      };
    }

    return undefined;
  }, [themeColors, isHovered, canSubmit, isLoading]);

  const hasCustomTheme = themeColors?.buttonBackground && themeColors?.buttonText;

  const emailOptInField = entryFields.find(f => f.field_type === "email_opt_in");
  const smsOptInField = entryFields.find(f => f.field_type === "sms_opt_in");
  const sponsorName = termsVariables?.sponsorName || "the sponsor's";
  const sponsorPossessive = sponsorName === "the sponsor's" ? sponsorName : `${sponsorName}'s`;
  const emailPrivacyUrl = emailOptInField?.validation_rules?.privacy_url || legalPrivacyUrl;
  const smsPrivacyUrl = smsOptInField?.validation_rules?.privacy_url || legalPrivacyUrl;
  const smsTermsUrl = smsOptInField?.validation_rules?.terms_url || legalTermsUrl;

  const renderEmailOptIn = () => {
    if (!emailOptInField) return null;
    return (
      <div key={emailOptInField.field_key} className="pt-2">
        <div className="flex items-start space-x-2">
          <Checkbox
            id={emailOptInField.field_key}
            checked={(fieldValues[emailOptInField.field_key] as boolean) || false}
            onCheckedChange={(checked) => handleFieldValueChange(emailOptInField.field_key, !!checked)}
            disabled={isLoading}
          />
          <label htmlFor={emailOptInField.field_key} className="text-sm font-medium leading-none cursor-pointer mt-0.5">
            {emailOptInField.label} {emailOptInField.is_required && "*"}
          </label>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 ml-6 leading-relaxed">
          {consentHelper?.trim()
            ? consentHelper
            : <>
              By subscribing you agree to receive marketing emails and {sponsorPossessive}{" "}
              {emailPrivacyUrl ? (
                <a href={emailPrivacyUrl} target="_blank" rel="noopener noreferrer" className="text-teal-600 underline-offset-2 hover:underline">
                  Privacy Policy
                </a>
              ) : (
                "Privacy Policy"
              )}. You can unsubscribe at any time.
            </>}
        </p>
        {formErrors[emailOptInField.field_key] && <p className="text-xs text-red-500 ml-6">{formErrors[emailOptInField.field_key]}</p>}
      </div>
    );
  };

  const renderSmsOptIn = () => {
    if (!smsOptInField) return null;
    return (
      <div key={smsOptInField.field_key} className="pt-4">
        <div className="flex items-start space-x-2">
          <Checkbox
            id={smsOptInField.field_key}
            checked={(fieldValues[smsOptInField.field_key] as boolean) || false}
            onCheckedChange={(checked) => handleFieldValueChange(smsOptInField.field_key, !!checked)}
            disabled={isLoading}
          />
          <label htmlFor={smsOptInField.field_key} className="text-sm font-medium leading-none cursor-pointer mt-0.5">
            {smsOptInField.label} {smsOptInField.is_required && "*"}
          </label>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 ml-6 leading-relaxed">
          By checking this box, you agree to receive recurring automated marketing text messages from {sponsorName} at the phone number provided. Consent is not a condition of purchase. Message and data rates may apply. Reply STOP to unsubscribe.
        </p>
        {(smsTermsUrl || smsPrivacyUrl) && (
          <p className="text-xs text-muted-foreground mt-1 ml-6 leading-relaxed">
            View our{" "}
            {smsTermsUrl ? (
              <a href={smsTermsUrl} target="_blank" rel="noopener noreferrer" className="text-teal-600 underline-offset-2 hover:underline">Terms</a>
            ) : "Terms"}
            {" "}and{" "}
            {smsPrivacyUrl ? (
              <a href={smsPrivacyUrl} target="_blank" rel="noopener noreferrer" className="text-teal-600 underline-offset-2 hover:underline">Privacy Policy</a>
            ) : "Privacy Policy"}.
          </p>
        )}
        {formErrors[smsOptInField.field_key] && <p className="text-xs text-red-500 ml-6">{formErrors[smsOptInField.field_key]}</p>}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="fade-in bg-card rounded-xl border border-border p-6 shadow-lg">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="w-5 h-5 text-primary" />
        <h3 className="font-heading font-bold text-lg">Enter to Win!</h3>
      </div>

      <p className="text-muted-foreground text-sm mb-4">
        Enter your email address to participate in this giveaway.
      </p>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email Address <span className="text-red-500 ml-0.5">*</span></Label>
          <Input
            id="email"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={handleEmailChange}
            required
            disabled={isLoading}
            className={cn(
              "w-full",
              error && "border-destructive focus-visible:ring-destructive"
            )}
          />
          {error && error !== "Please fill in all required fields before continuing." && (
            <p className="text-destructive text-sm font-medium animate-in slide-in-from-top-1">{error}</p>
          )}
        </div>

        {entryFields.map(field => {
          if (field.field_type === "email_opt_in") {
            return renderEmailOptIn();
          }
          if (field.field_type === "sms_opt_in") {
            return renderSmsOptIn();
          }
          if (field.field_type === "name") {
            return (
              <div key={field.field_key} className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First name {field.is_required && "*"}</Label>
                    <Input
                      id="first_name"
                      type="text"
                      placeholder="First name"
                      value={(fieldValues["first_name"] as string) || ""}
                      onChange={(e) => handleFieldValueChange("first_name", e.target.value)}
                      disabled={isLoading}
                      autoComplete="given-name"
                      className={cn(formErrors["first_name"] && "border-destructive focus-visible:ring-destructive")}
                    />
                    {formErrors["first_name"] && <p className="text-xs text-red-500 mt-1">{formErrors["first_name"]}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last name {field.is_required && "*"}</Label>
                    <Input
                      id="last_name"
                      type="text"
                      placeholder="Last name"
                      value={(fieldValues["last_name"] as string) || ""}
                      onChange={(e) => handleFieldValueChange("last_name", e.target.value)}
                      disabled={isLoading}
                      autoComplete="family-name"
                      className={cn(formErrors["last_name"] && "border-destructive focus-visible:ring-destructive")}
                    />
                    {formErrors["last_name"] && <p className="text-xs text-red-500 mt-1">{formErrors["last_name"]}</p>}
                  </div>
                </div>
              </div>
            );
          } else if (field.field_type === "phone") {
            const rawPhone = (fieldValues[field.field_key] as string) || "";
            const formattedPhone = rawPhone.length > 0
              ? rawPhone.length <= 3
                ? `(${rawPhone}`
                : rawPhone.length <= 6
                  ? `(${rawPhone.slice(0, 3)}) ${rawPhone.slice(3)}`
                  : `(${rawPhone.slice(0, 3)}) ${rawPhone.slice(3, 6)}-${rawPhone.slice(6, 10)}`
              : "";

            return (
              <div key={field.field_key} className="space-y-2">
                <Label htmlFor={field.field_key}>{field.label} {field.is_required && "*"}</Label>
                <Input
                  id={field.field_key}
                  type="tel"
                  placeholder={field.placeholder || "(555) 555-5555"}
                  value={formattedPhone}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    handleFieldValueChange(field.field_key, digits);
                  }}
                  disabled={isLoading}
                  className={cn(formErrors[field.field_key] && "border-destructive focus-visible:ring-destructive")}
                />
                {formErrors[field.field_key] && <p className="text-xs text-red-500 mt-1">{formErrors[field.field_key]}</p>}
              </div>
            );
          } else if (field.field_type === "address") {
            return (
              <div key={field.field_key} className="space-y-4">
                <Label className="text-base">{field.label} {field.is_required && "*"}</Label>
                <div className="space-y-3 mt-2">
                  <div className="space-y-1">
                    <Input
                      placeholder="Street address"
                      value={(fieldValues["address_line1"] as string) || ""}
                      onChange={(e) => handleFieldValueChange("address_line1", e.target.value)}
                      disabled={isLoading}
                      className={cn(formErrors["address_line1"] && "border-destructive")}
                    />
                    {formErrors["address_line1"] && <p className="text-xs text-red-500">{formErrors["address_line1"]}</p>}
                  </div>
                  <div className="space-y-1">
                    <Input
                      placeholder="Apt, suite, etc. (optional)"
                      value={(fieldValues["address_line2"] as string) || ""}
                      onChange={(e) => handleFieldValueChange("address_line2", e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1">
                      <Input
                        placeholder="City"
                        value={(fieldValues["address_city"] as string) || ""}
                        onChange={(e) => handleFieldValueChange("address_city", e.target.value)}
                        disabled={isLoading}
                        className={cn(formErrors["address_city"] && "border-destructive")}
                      />
                      {formErrors["address_city"] && <p className="text-xs text-red-500">{formErrors["address_city"]}</p>}
                    </div>
                    <div className="space-y-1">
                      <Select
                        value={(fieldValues["address_state"] as string) || ""}
                        onValueChange={(val) => handleFieldValueChange("address_state", val)}
                        disabled={isLoading}
                      >
                        <SelectTrigger className={cn(formErrors["address_state"] && "border-destructive")}>
                          <SelectValue placeholder="State" />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {[
                            ['Alabama', 'AL'], ['Alaska', 'AK'], ['Arizona', 'AZ'], ['Arkansas', 'AR'],
                            ['California', 'CA'], ['Colorado', 'CO'], ['Connecticut', 'CT'], ['Delaware', 'DE'],
                            ['District of Columbia', 'DC'], ['Florida', 'FL'], ['Georgia', 'GA'], ['Hawaii', 'HI'],
                            ['Idaho', 'ID'], ['Illinois', 'IL'], ['Indiana', 'IN'], ['Iowa', 'IA'],
                            ['Kansas', 'KS'], ['Kentucky', 'KY'], ['Louisiana', 'LA'], ['Maine', 'ME'],
                            ['Maryland', 'MD'], ['Massachusetts', 'MA'], ['Michigan', 'MI'], ['Minnesota', 'MN'],
                            ['Mississippi', 'MS'], ['Missouri', 'MO'], ['Montana', 'MT'], ['Nebraska', 'NE'],
                            ['Nevada', 'NV'], ['New Hampshire', 'NH'], ['New Jersey', 'NJ'], ['New Mexico', 'NM'],
                            ['New York', 'NY'], ['North Carolina', 'NC'], ['North Dakota', 'ND'], ['Ohio', 'OH'],
                            ['Oklahoma', 'OK'], ['Oregon', 'OR'], ['Pennsylvania', 'PA'], ['Rhode Island', 'RI'],
                            ['South Carolina', 'SC'], ['South Dakota', 'SD'], ['Tennessee', 'TN'], ['Texas', 'TX'],
                            ['Utah', 'UT'], ['Vermont', 'VT'], ['Virginia', 'VA'], ['Washington', 'WA'],
                            ['West Virginia', 'WV'], ['Wisconsin', 'WI'], ['Wyoming', 'WY']
                          ].map(([name, abbr]) => (
                            <SelectItem key={abbr} value={abbr}>
                              {name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formErrors["address_state"] && <p className="text-xs text-red-500">{formErrors["address_state"]}</p>}
                    </div>
                    <div className="space-y-1">
                      <Input
                        placeholder="ZIP"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={(fieldValues["address_zip"] as string) || ""}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, '').slice(0, 5);
                          handleFieldValueChange("address_zip", digits);
                        }}
                        disabled={isLoading}
                        className={cn(formErrors["address_zip"] && "border-destructive")}
                      />
                      {formErrors["address_zip"] && <p className="text-xs text-red-500">{formErrors["address_zip"]}</p>}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Input
                      placeholder="Country"
                      value="United States 🇺🇸"
                      readOnly
                      disabled={isLoading}
                      className="bg-muted/50 cursor-default"
                    />
                  </div>
                </div>
              </div>
            );
          } else if (field.field_type === "custom") {
            const inputType = getCustomInputType(field);

            if (inputType === "checkbox") {
              return (
                <div key={field.field_key} className="pt-1">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id={field.field_key}
                      checked={(fieldValues[field.field_key] as boolean) || false}
                      onCheckedChange={(checked) => handleFieldValueChange(field.field_key, !!checked)}
                      disabled={isLoading}
                    />
                    <label htmlFor={field.field_key} className="text-sm font-medium leading-none cursor-pointer mt-0.5">
                      {field.label} {field.is_required && "*"}
                    </label>
                  </div>
                  {formErrors[field.field_key] && <p className="text-xs text-red-500 ml-6 mt-1">{formErrors[field.field_key]}</p>}
                </div>
              );
            }

            if (inputType === "dropdown") {
              const options = getDropdownOptions(field);
              return (
                <div key={field.field_key} className="space-y-2">
                  <Label htmlFor={field.field_key}>{field.label} {field.is_required && "*"}</Label>
                  <Select
                    value={(fieldValues[field.field_key] as string) || undefined}
                    onValueChange={(val) => handleFieldValueChange(field.field_key, val)}
                    disabled={isLoading}
                  >
                    <SelectTrigger id={field.field_key} className={cn(formErrors[field.field_key] && "border-destructive")}>
                      <SelectValue placeholder={field.placeholder || "Select..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {options.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formErrors[field.field_key] && <p className="text-xs text-red-500">{formErrors[field.field_key]}</p>}
                </div>
              );
            }

            if (inputType === "date") {
              return (
                <DateSelectField
                  key={field.field_key}
                  id={field.field_key}
                  label={field.label}
                  required={field.is_required}
                  mode={getDateMode(field)}
                  range={getDateRange(field)}
                  value={(fieldValues[field.field_key] as string) || ""}
                  disabled={isLoading}
                  error={formErrors[field.field_key]}
                  onChange={(val) => handleFieldValueChange(field.field_key, val)}
                />
              );
            }

            return (
              <div key={field.field_key} className="space-y-2">
                <Label htmlFor={field.field_key}>{field.label} {field.is_required && "*"}</Label>
                <Input
                  id={field.field_key}
                  type="text"
                  placeholder={field.placeholder || ""}
                  value={(fieldValues[field.field_key] as string) || ""}
                  onChange={(e) => handleFieldValueChange(field.field_key, e.target.value)}
                  disabled={isLoading}
                  className={cn(formErrors[field.field_key] && "border-destructive")}
                />
                {formErrors[field.field_key] && <p className="text-xs text-red-500 mt-1">{formErrors[field.field_key]}</p>}
              </div>
            );
          }
          return null;
        })}

        {error === "Please fill in all required fields before continuing." && (
          <p className="text-destructive text-sm font-medium animate-in slide-in-from-top-1 text-center bg-destructive/10 p-2 rounded">
            {error}
          </p>
        )}

        {requireTermsAcceptance && termsVariables && (
          <div className="flex items-start space-x-2 pt-2">
            <Checkbox
              id="terms"
              checked={termsAccepted}
              onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
            />
            <div className="grid gap-1.5 leading-none">
              <label
                htmlFor="terms"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                I agree to the{" "}
                <TermsModal
                  termsVariables={termsVariables}
                  referralBonusEntries={referralBonusEntries || 1}
                  endsAt={endsAt}
                  startsAt={startsAt}
                  useCustomTerms={useCustomTerms}
                  customTermsText={customTermsText}
                >
                  <button type="button" className="text-primary hover:underline underline-offset-4 font-semibold inline-flex items-center gap-1 align-baseline">
                    Terms & Conditions
                  </button>
                </TermsModal>
              </label>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || !canSubmit}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={buttonStyles}
          className={cn(
            "w-full h-12 rounded-lg font-semibold transition-all duration-200",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed",
            !isLoading && canSubmit && "hover:scale-[1.02] active:scale-[0.98]",
            "flex items-center justify-center gap-2",
            !hasCustomTheme && "bg-gradient-accent text-accent-foreground shadow-lg hover:shadow-accent-glow",
            hasCustomTheme && "shadow-lg hover:shadow-xl"
          )}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting...
            </>
          ) : (
            "Continue"
          )}
        </button>
      </div>
    </form>
  );
}
